import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    alpha,
    Avatar,
    Badge,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Menu as MenuIcon,
    NotificationsNone as NotificationsIcon,
    Brightness4 as Brightness4Icon,
    Brightness7 as Brightness7Icon,
    Settings as SettingsIcon,
    HelpOutline as HelpIcon,
    Logout as LogoutIcon,
    ExpandMore as ExpandMoreIcon,
    CreditCard as BillingIcon,
    Star as PricingIcon,
    AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import Navigation from './Navigation';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { FEATURES } from '../constants/features';

const SIDEBAR_WIDTH = 280;

const MainLayout = ({ children, isDarkMode, onToggleTheme }) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const [currentUser, setCurrentUser] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const { isFeatureEnabled } = useFeatureToggles();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        handleMenuClose();
        try {
            await signOut(auth);
            navigate('/');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                try {
                    const token = await user.getIdTokenResult();
                    setIsSuperAdmin(!!token.claims.super_admin);
                } catch (err) { }
            } else {
                setIsSuperAdmin(false);
            }
        });
        return () => unsubscribe();
    }, []);

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const sidebarContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
            {/* Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6, px: 1 }}>
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 900,
                        fontSize: '1.2rem',
                        boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.5)',
                    }}
                >
                    P
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.02em', color: theme.palette.text.primary }}>
                    Socialixer
                </Typography>
            </Box>

            {/* Navigation */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                <Navigation isSidebar={true} onCloseDrawer={() => setMobileOpen(false)} />
            </Box>

            {/* User Profile Dropdown */}
            <Box sx={{ mt: 'auto', flexShrink: 0 }}>
                <Box
                    onClick={handleMenuOpen}
                    sx={{
                        p: 1.5,
                        borderRadius: 4,
                        background: anchorEl ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.1),
                        }
                    }}
                >
                    <Avatar
                        src={currentUser?.photoURL}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            width: 40,
                            height: 40,
                            fontSize: '0.9rem',
                            fontWeight: 700
                        }}
                    >
                        {getInitials(currentUser?.displayName || currentUser?.email)}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 800, color: 'text.primary' }}>
                            {currentUser?.displayName || 'Active User'}
                        </Typography>
                        <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
                            {currentUser?.email || 'Logged In'}
                        </Typography>
                    </Box>
                    <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </Box>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    PaperProps={{
                        sx: {
                            mt: -1,
                            width: 248,
                            borderRadius: 3,
                            boxShadow: `0 10px 40px ${alpha(theme.palette.common.black, 0.1)}`,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            bgcolor: 'background.paper',
                        }
                    }}
                >
                    <MenuItem
                        component={Link}
                        to="/app/guide"
                        onClick={() => { handleMenuClose(); setMobileOpen(false); }}
                        selected={location.pathname === '/app/guide'}
                        sx={{ py: 1.5, mb: 0.5, borderRadius: 2, mx: 1 }}
                    >
                        <ListItemIcon><HelpIcon fontSize="small" color={location.pathname === '/app/guide' ? 'primary' : 'inherit'} /></ListItemIcon>
                        <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>Setup Guide</Typography>} />
                    </MenuItem>

                    {isSuperAdmin && (
                        <MenuItem
                            component={Link}
                            to="/app/admin/toggles"
                            onClick={() => { handleMenuClose(); setMobileOpen(false); }}
                            selected={location.pathname === '/app/admin/toggles'}
                            sx={{ py: 1.5, mb: 0.5, borderRadius: 2, mx: 1 }}
                        >
                            <ListItemIcon><AdminIcon fontSize="small" color={location.pathname === '/app/admin/toggles' ? 'primary' : 'inherit'} /></ListItemIcon>
                            <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>Admin Settings</Typography>} />
                        </MenuItem>
                    )}

                    <MenuItem
                        component={Link}
                        to="/app/settings"
                        onClick={() => { handleMenuClose(); setMobileOpen(false); }}
                        selected={location.pathname === '/app/settings'}
                        sx={{ py: 1.5, mb: 0.5, borderRadius: 2, mx: 1 }}
                    >
                        <ListItemIcon><SettingsIcon fontSize="small" color={location.pathname === '/app/settings' ? 'primary' : 'inherit'} /></ListItemIcon>
                        <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>Settings</Typography>} />
                    </MenuItem>

                    {isFeatureEnabled(FEATURES.BILLING) && (
                        <>
                            <Box sx={{ my: 1, mx: 2, height: '1px', bgcolor: alpha(theme.palette.divider, 0.1) }} />
                            <MenuItem
                                component={Link}
                                to="/app/pricing"
                                onClick={() => { handleMenuClose(); setMobileOpen(false); }}
                                selected={location.pathname === '/app/pricing'}
                                sx={{ py: 1.5, mb: 0.5, borderRadius: 2, mx: 1 }}
                            >
                                <ListItemIcon><PricingIcon fontSize="small" color={location.pathname === '/app/pricing' ? 'primary' : 'inherit'} /></ListItemIcon>
                                <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>Pricing & Plans</Typography>} />
                            </MenuItem>
                            <MenuItem
                                component={Link}
                                to="/app/billing"
                                onClick={() => { handleMenuClose(); setMobileOpen(false); }}
                                selected={location.pathname === '/app/billing'}
                                sx={{ py: 1.5, mb: 0.5, borderRadius: 2, mx: 1 }}
                            >
                                <ListItemIcon><BillingIcon fontSize="small" color={location.pathname === '/app/billing' ? 'primary' : 'inherit'} /></ListItemIcon>
                                <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>Billing & Subscription</Typography>} />
                            </MenuItem>
                        </>
                    )}

                    <Box sx={{ my: 1, mx: 2, height: '1px', bgcolor: alpha(theme.palette.divider, 0.1) }} />
                    <MenuItem
                        onClick={handleLogout}
                        sx={{ py: 1.5, borderRadius: 2, mx: 1, color: 'error.main' }}
                    >
                        <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>Log out</Typography>} />
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
            {/* Sidebar - Desktop */}
            {!isMobile && (
                <Box
                    component="nav"
                    sx={{
                        width: SIDEBAR_WIDTH,
                        flexShrink: 0,
                        borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.4),
                        backdropFilter: 'blur(20px)',
                        position: 'fixed',
                        height: '100vh',
                        zIndex: theme.zIndex.appBar + 1,
                    }}
                >
                    {sidebarContent}
                </Box>
            )}

            {/* Sidebar - Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: SIDEBAR_WIDTH,
                        bgcolor: theme.palette.background.paper,
                        backgroundImage: 'none'
                    },
                }}
            >
                {sidebarContent}
            </Drawer>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { lg: `calc(100% - ${SIDEBAR_WIDTH}px)` },
                    ml: { lg: `${SIDEBAR_WIDTH}px` },
                    transition: 'all 0.3s ease',
                }}
            >
                {/* TopBar */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        bgcolor: alpha(theme.palette.background.default, 0.7),
                        backdropFilter: 'blur(12px)',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                        color: theme.palette.text.primary,
                    }}
                >
                    <Toolbar sx={{ px: { xs: 2, sm: 4 }, gap: 2 }}>
                        {isMobile && (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}


                        <Box sx={{ flexGrow: 1 }} />

                        <IconButton onClick={onToggleTheme} sx={{ color: 'text.secondary' }}>
                            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>

                        <IconButton sx={{ color: 'text.secondary' }}>
                            <Badge badgeContent={3} color="primary" variant="dot">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* Dynamic Page Content */}
                <Box sx={{ p: { xs: 2, sm: 4, md: 6 }, maxWidth: '1600px', mx: 'auto' }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default MainLayout;
